import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ReferenceAuthorNameListPipe } from 'shared/util-common';

import {
  BlogPostReference,
  BookReference,
  BookSectionReference,
  ConferencePaperReference,
  JournalReference,
  ReferenceType,
  ReferenceWithNumber,
  ReportReference,
  WebPageReference,
} from '@treetop/shared/model';

@Component({
  selector: 'tt-reference',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './reference.component.html',
  host: {
    '[attr.data-reference-title]': 'reference().title',
    '[attr.data-reference-number]': 'reference().referenceNumber',
  },
})
export class ReferenceComponent {
  private referenceAuthorNameListPipe = inject(ReferenceAuthorNameListPipe, { optional: true }) ?? new ReferenceAuthorNameListPipe();

  public reference = input.required<ReferenceWithNumber>();

  protected readonly ReferenceType = ReferenceType;

  protected authors = computed(() => this.referenceAuthorNameListPipe.transform(this.reference().authors?.filter(Boolean), 6));

  protected titleLink = computed(() => {
    const ref = this.reference();
    return ref.url ?? (ref.doi ? `https://doi.org/${ref.doi}` : null);
  });

  protected details = computed(() => this.getDetails(this.reference()));

  private getDetails(reference: ReferenceWithNumber): string {
    switch (reference.referenceType) {
      case ReferenceType.JournalArticle:
      case ReferenceType.MagazineArticle:
        return this.journalDetails(reference as JournalReference);
      case ReferenceType.WebPage:
        return this.webPageDetails(reference as WebPageReference);
      case ReferenceType.Book:
        return this.bookDetails(reference as BookReference);
      case ReferenceType.ConferencePaper:
        return this.conferencePaperDetails(reference as ConferencePaperReference);
      case ReferenceType.BlogPost:
        return this.blogPostDetails(reference as BlogPostReference);
      case ReferenceType.Report:
        return this.reportDetails(reference as ReportReference);
      case ReferenceType.BookSection:
        return this.bookSectionDetails(reference as BookSectionReference);
      default:
        return '';
    }
  }

  private journalDetails(r: JournalReference): string {
    let s = '';
    if (r.extendedContent.journalAbbreviation) s += `${r.extendedContent.journalAbbreviation}. `;
    if (r.publishDate) s += `${new Date(r.publishDate).getFullYear()};`;
    if (r.extendedContent.volume) s += `${r.extendedContent.volume}`;
    if (r.extendedContent.issue) s += `(${r.extendedContent.issue})${r.extendedContent.pages ? ':' : ''}`;
    if (r.extendedContent.pages) s += `${r.extendedContent.pages}.`;
    if (r.doi) s += ` doi:${r.doi}`;
    return s;
  }

  private webPageDetails(r: WebPageReference): string {
    let s = '';
    if (r.extendedContent.websiteTitle) s += `${r.extendedContent.websiteTitle}.`;
    if (r.url) s += ` ${r.url}.`;
    if (r.publishDate) s += ` ${new Date(r.publishDate).getFullYear()}.`;
    return s;
  }

  private bookDetails(r: BookReference): string {
    let s = '';
    if (r.extendedContent.edition) s += `${r.extendedContent.edition}.`;
    if (r.extendedContent.place) s += ` ${r.extendedContent.place}:`;
    if (r.extendedContent.publisher) s += ` ${r.extendedContent.publisher};`;
    if (r.publishDate) s += ` ${new Date(r.publishDate).getFullYear()}.`;
    return s;
  }

  private conferencePaperDetails(r: ConferencePaperReference): string {
    let s = '';
    if (r.extendedContent.conferenceName) s += `${r.extendedContent.conferenceName}. `;
    if (r.extendedContent.place) s += `${r.extendedContent.place}. `;
    if (r.extendedContent.volume) s += `${r.extendedContent.volume}${r.extendedContent.pages ? ':' : ''}`;
    if (r.extendedContent.pages) s += `${r.extendedContent.pages}. `;
    return s;
  }

  private blogPostDetails(r: BlogPostReference): string {
    let s = '';
    if (r.extendedContent.blogTitle) s += `${r.extendedContent.blogTitle}.`;
    if (r.url) s += `${r.url}.`;
    return s;
  }

  private reportDetails(r: ReportReference): string {
    let s = '';
    if (r.extendedContent.reportNumber) s += `${r.extendedContent.reportNumber}.`;
    if (r.extendedContent.reportType) s += ` ${r.extendedContent.reportType}.`;
    if (r.extendedContent.place) s += ` ${r.extendedContent.place}:`;
    if (r.extendedContent.institution) s += ` ${r.extendedContent.institution};`;
    if (r.publishDate) s += ` ${new Date(r.publishDate).getFullYear()}`;
    if (r.extendedContent.pages) s += `:${r.extendedContent.pages}.`;
    return s;
  }

  private bookSectionDetails(r: BookSectionReference): string {
    let s = '';
    if (r.extendedContent.bookTitle) s += `In: ${r.extendedContent.bookTitle}.`;
    if (r.extendedContent.edition) s += ` ${r.extendedContent.edition}.`;
    if (r.extendedContent.place) s += ` ${r.extendedContent.place}:`;
    if (r.extendedContent.publisher) s += ` ${r.extendedContent.publisher};`;
    if (r.publishDate) s += ` ${new Date(r.publishDate).getFullYear()}:`;
    if (r.extendedContent.pages) s += `${r.extendedContent.pages}.`;
    return s;
  }
}
